import { WORKER_API_URL } from './config';
import {
  DocumentMetadata,
  PresignedUrlResponse,
  Document,
  DocumentListResponse,
  ViewUrlResponse,
  QuotaInfo,
  DocumentStats,
  // DeleteResponse,
  DocumentFilters,
  // UploadRequest,
  UploadCompleteResponse,
  ProcessingStatusResponse,
  ReprocessRequest,
  ReprocessResponse,
  // BatchDeleteRequest,
  BatchDeleteResponse
} from '../types/document';

class DocumentService {
  private baseUrl = `${WORKER_API_URL}/documents`;

  private getHeaders(authHeaders?: Record<string, string>): HeadersInit {
    // If auth headers are provided (from AuthContext), use them
    if (authHeaders && authHeaders.Authorization) {
      // Debug logging removed
      return {
        ...authHeaders,
        'Content-Type': 'application/json'
      };
    }
    
    // Fallback to localStorage token (same as subscription.ts pattern)
    const token = localStorage.getItem('token');
    // Debug logging removed
    
    if (!token) {
      throw new Error('No authentication token available. Please log in.');
    }
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  private async handleApiResponse(response: Response): Promise<any> {
    // Debug logging removed
    
    if (response.status === 401) {
      console.error('[documentService] 401 Unauthorized response');
      // Don't immediately clear token - let AuthContext handle token lifecycle
      // This prevents race conditions where a temporary 401 clears a valid token
      try {
        const errorData = await response.json();
        console.error('[documentService] 401 error details:', errorData);
        
        // Only clear token if explicitly told the token is invalid/expired
        if (errorData.error?.includes('expired') || errorData.error?.includes('invalid')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      } catch (e) {
        console.error('[documentService] Failed to parse 401 error response');
      }
      throw new Error('Authentication failed. Please try refreshing the page.');
    }
    
    if (response.status === 403) {
      throw new Error('You do not have permission to perform this action.');
    }
    
    if (!response.ok) {
      let errorMessage = 'An error occurred';
      try {
        const error = await response.json();
        errorMessage = error.detail || error.error || error.message || 'Failed to process request';
      } catch {
        errorMessage = `Request failed with status ${response.status}`;
      }
      throw new Error(errorMessage);
    }
    
    return response.json();
  }

  async requestUploadUrl(metadata: DocumentMetadata, authHeaders?: Record<string, string>): Promise<PresignedUrlResponse> {
    // Debug logging removed
    
    const response = await fetch(`${this.baseUrl}/upload-url`, {
      method: 'POST',
      headers: this.getHeaders(authHeaders),
      body: JSON.stringify({ metadata })
    });

    const result = await this.handleApiResponse(response);
    // Debug logging removed
    
    return result;
  }

  async uploadToR2(
    url: string, 
    fields: Record<string, string>, 
    file: File, 
    onProgress?: (progress: number) => void,
    method: 'POST' | 'PUT' = 'POST',
    headers?: Record<string, string>
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Set up progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      // Set up response handling
      xhr.addEventListener('load', () => {
        // Debug logging removed

        if (xhr.status >= 200 && xhr.status < 300) {
          // Extract ETag from response headers if available
          const etag = xhr.getResponseHeader('ETag');
          // Debug logging removed
          resolve(etag || '');
        } else {
          console.error('[documentService] Upload failed:', {
            status: xhr.status,
            statusText: xhr.statusText,
            responseText: xhr.responseText
          });
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      // Handle different upload methods
      // Debug logging removed
      
      if (method === 'PUT') {
        // For PUT requests (R2), send file directly with headers
        // Debug logging removed
        
        xhr.open('PUT', url);
        
        // Set any required headers
        if (headers) {
          Object.entries(headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value);
          });
        }
        
        // Send file directly for PUT
        xhr.send(file);
      } else {
        // For POST requests (legacy), use FormData
        // Debug logging removed
        const formData = new FormData();
        
        // Add all fields from the presigned URL response
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        
        // File must be the last field
        formData.append('file', file);
        
        xhr.open('POST', url);
        // Don't set any headers for POST - they're in the presigned URL
        xhr.send(formData);
      }
    });
  }

  async confirmUpload(documentId: string, etag?: string, authHeaders?: Record<string, string>): Promise<Document> {
    // Debug logging removed

    const headers = this.getHeaders(authHeaders);
    // Debug logging removed

    const body = JSON.stringify({ etag });
    // Debug logging removed

    const response = await fetch(`${this.baseUrl}/${documentId}/complete`, {
      method: 'POST',
      headers,
      body
    });

    // Debug logging removed

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[documentService] Confirm upload error response body:', errorText);
      try {
        const errorJson = JSON.parse(errorText);
        console.error('[documentService] Parsed error:', errorJson);
      } catch (e) {
        console.error('[documentService] Error response is not JSON');
      }
    }

    const result: UploadCompleteResponse = await this.handleApiResponse(response);
    return result.document;
  }

  async listDocuments(filters: DocumentFilters = {}, authHeaders?: Record<string, string>): Promise<DocumentListResponse> {
    const params = new URLSearchParams();
    
    if (filters.courseId) params.append('courseId', filters.courseId);
    if (filters.documentType) params.append('documentType', filters.documentType);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const url = `${this.baseUrl}?${params.toString()}`;
    const headers = this.getHeaders(authHeaders);
    
    // Debug logging removed

    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
      credentials: 'include'
    });

    // Debug logging removed

    return this.handleApiResponse(response);
  }

  async getDocument(documentId: string, authHeaders?: Record<string, string>): Promise<Document> {
    const response = await fetch(`${this.baseUrl}/${documentId}`, {
      headers: this.getHeaders(authHeaders)
    });

    return this.handleApiResponse(response);
  }

  async getViewUrl(documentId: string, authHeaders?: Record<string, string>): Promise<ViewUrlResponse> {
    const response = await fetch(`${this.baseUrl}/${documentId}/view-url`, {
      headers: this.getHeaders(authHeaders)
    });

    return this.handleApiResponse(response);
  }

  async deleteDocument(documentId: string, authHeaders?: Record<string, string>): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${documentId}`, {
      method: 'DELETE',
      headers: this.getHeaders(authHeaders)
    });

    await this.handleApiResponse(response);
  }

  async batchDeleteDocuments(documentIds: string[], authHeaders?: Record<string, string>): Promise<BatchDeleteResponse> {
    const response = await fetch(`${this.baseUrl}/batch`, {
      method: 'DELETE',
      headers: this.getHeaders(authHeaders),
      body: JSON.stringify({
        document_ids: documentIds
      })
    });

    return this.handleApiResponse(response);
  }

  async getUserQuota(authHeaders?: Record<string, string>): Promise<QuotaInfo> {
    const response = await fetch(`${this.baseUrl}/quota`, {
      headers: this.getHeaders(authHeaders)
    });

    return this.handleApiResponse(response);
  }

  async getDocumentStats(authHeaders?: Record<string, string>): Promise<DocumentStats> {
    const response = await fetch(`${this.baseUrl}/stats`, {
      headers: this.getHeaders(authHeaders)
    });

    return this.handleApiResponse(response);
  }

  // Helper method to validate file before upload
  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > 50 * 1024 * 1024) {
      return { valid: false, error: 'File size exceeds 50MB limit' };
    }

    // Check file type
    const supportedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ];

    if (!supportedTypes.includes(file.type)) {
      return { valid: false, error: 'File type not supported' };
    }

    return { valid: true };
  }

  // Helper method to format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    // Do not show decimals for KB to keep it clean (e.g., 250 KB)
    const decimals = sizes[i] === 'KB' ? 0 : 2;
    const formatted = Number(value.toFixed(decimals));
    return `${formatted} ${sizes[i]}`;
  }

  // List deleted documents that can be recovered
  async listDeletedDocuments(skip: number = 0, limit: number = 20, authHeaders?: Record<string, string>): Promise<{
    documents: any[];
    total: number;
    skip: number;
    limit: number;
  }> {
    const response = await fetch(
      `${this.baseUrl}/deleted?skip=${skip}&limit=${limit}`,
      {
        headers: this.getHeaders(authHeaders),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch deleted documents');
    }

    return response.json();
  }

  // Recover a deleted document
  async recoverDocument(documentId: number, authHeaders?: Record<string, string>): Promise<Document> {
    const response = await fetch(
      `${this.baseUrl}/${documentId}/recover`,
      {
        method: 'POST',
        headers: this.getHeaders(authHeaders),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      if (response.status === 410) {
        throw new Error('Recovery period has expired (30 days)');
      }
      throw new Error(error.detail || 'Failed to recover document');
    }

    return response.json();
  }

  // Get processing status for a document
  async getProcessingStatus(documentId: string | number, authHeaders?: Record<string, string>): Promise<ProcessingStatusResponse> {
    const response = await fetch(`${this.baseUrl}/${documentId}/processing-status`, {
      headers: this.getHeaders(authHeaders)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get processing status');
    }

    return response.json();
  }

  // Reprocess a document
  async reprocessDocument(documentId: string | number, request?: ReprocessRequest, authHeaders?: Record<string, string>): Promise<ReprocessResponse> {
    // Debug logging removed
    const response = await fetch(`${this.baseUrl}/${documentId}/reprocess`, {
      method: 'POST',
      headers: this.getHeaders(authHeaders),
      body: JSON.stringify(request || {})
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('SQS_DEBUGGING: Reprocess API error:', error);
      throw new Error(error.detail || 'Failed to reprocess document');
    }

    const result = await response.json();
    // Debug logging removed
    return result;
  }


  // Get processing history for a document
  async getProcessingHistory(documentId: number, authHeaders?: Record<string, string>): Promise<{ data: any[] }> {
    const response = await fetch(`${this.baseUrl}/${documentId}/processing-history`, {
      headers: this.getHeaders(authHeaders)
    });

    if (!response.ok) {
      return { data: [] };
    }

    const result = await response.json();
    // Backend returns { data: [...] }, so extract the data array
    return { data: result.data || [] };
  }

  // Get SQS queue status
  async getQueueStatus(authHeaders?: Record<string, string>): Promise<{ data: any }> {
    const response = await fetch(`${this.baseUrl}/queue-status`, {
      headers: this.getHeaders(authHeaders)
    });

    if (!response.ok) {
      return { data: null };
    }

    const result = await response.json();
    // Backend returns { data: {...} }, so extract the data object
    return { data: result.data || null };
  }

  // Get SQS messages for a document
  async getSQSMessages(documentId: number, authHeaders?: Record<string, string>): Promise<{ data: any[] }> {
    const response = await fetch(`${this.baseUrl}/${documentId}/sqs-messages`, {
      headers: this.getHeaders(authHeaders)
    });

    if (!response.ok) {
      return { data: [] };
    }

    const result = await response.json();
    // Backend returns { data: [...] }, so extract the data array
    return { data: result.data || [] };
  }
}

const documentService = new DocumentService();
export default documentService;